// SubtestRangeRecommendButton.js

import React, { useCallback, useMemo, useState } from "react";
import axios from "axios";

import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";

import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const baseURL = process.env.REACT_APP_API_URL || "http://localhost:8000";

const buildLabel = (row) => {
  let gender = row.gender || "Any";

  let age = "All Ages";

  if (row.min_age && row.max_age) {
    age = `${row.min_age} - ${row.max_age}`;
  } else if (row.min_age) {
    age = `${row.min_age}+`;
  } else if (row.max_age) {
    age = `0 - ${row.max_age}`;
  }

  return `${gender} | ${age}`;
};

const SubtestRangeRecommendButton = ({
  licenseKey,
  category,
  subtest,
  onApply,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [selected, setSelected] = useState([]);

  const fetchSuggestions = useCallback(async () => {
    if (!licenseKey || !category || !subtest?.sub_test_name) return;

    setLoading(true);
    setError(null);

    try {
      const res = await axios.post(
        `${baseURL}/emis_pathology/api/subtests/range_recommend/`,
        {
          license_key: licenseKey,
          category,
          subtest_name: subtest.sub_test_name,
        },
      );

      const data = res.data || [];

      setSuggestions(data);
      setSelected(data.map((_, idx) => idx));
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          "Failed to fetch suggestions.",
      );
    } finally {
      setLoading(false);
    }
  }, [licenseKey, category, subtest]);

  const handleOpen = () => {
    setOpen(true);
    fetchSuggestions();
  };

  const handleClose = () => {
    setOpen(false);
    setSuggestions([]);
    setSelected([]);
    setError(null);
  };

  const toggleRow = (idx) => {
    setSelected((prev) =>
      prev.includes(idx) ? prev.filter((x) => x !== idx) : [...prev, idx],
    );
  };

  const allSelected =
    suggestions.length > 0 && selected.length === suggestions.length;

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelected([]);
    } else {
      setSelected(suggestions.map((_, idx) => idx));
    }
  };

  const selectedRows = useMemo(
    () => suggestions.filter((_, idx) => selected.includes(idx)),
    [suggestions, selected],
  );

  const handleApply = () => {
    if (!selectedRows.length) return;
    onApply(selectedRows);
    handleClose();
  };

  return (
    <>
      <Tooltip title="Suggest common ranges for this subtest">
        <span>
          <Button
            size="small"
            variant="outlined"
            disabled={disabled}
            startIcon={<AutoAwesomeIcon sx={{ fontSize: 14 }} />}
            onClick={handleOpen}
            sx={{
              fontSize: "0.6rem",
              py: 0.2,
              minWidth: 0,
              px: 1,
              mt: "-5px",
              borderColor: "#7c4dff",
              color: "#7c4dff",
              borderRadius: "6px",
              whiteSpace: "nowrap",
              "&:hover": {
                background: "#7c4dff",
                color: "#fff",
                borderColor: "#7c4dff",
              },
              "&:disabled": {
                borderColor: "#ccc",
                color: "#bbb",
              },
            }}
          ></Button>
        </span>
      </Tooltip>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            border: "1.5px solid #ede7f6",
            boxShadow: "0 8px 40px rgba(124,77,255,0.12)",
          },
        }}
      >
        {/* Header */}
        <DialogTitle
          sx={{
            background: "linear-gradient(135deg, #7c4dff 0%, #5c35cc 100%)",
            color: "#fff",
            py: 1.5,
            px: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderRadius: "10px 10px 0 0",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AutoAwesomeIcon sx={{ fontSize: 18 }} />
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: "0.95rem" }}>
                Suggested Ranges
              </Typography>
              <Typography sx={{ fontSize: "0.75rem", opacity: 0.85 }}>
                {subtest?.sub_test_name}
              </Typography>
            </Box>
          </Box>
          <IconButton size="small" onClick={handleClose} sx={{ color: "#fff" }}>
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 2.5, pt: 2, pb: 1 }}>
          {/* Context */}
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1,
              mb: 1.5,
              mt: 1.5,
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 14, color: "#888", mt: "2px" }} />
            <Typography
              sx={{ fontSize: "0.75rem", color: "#888", lineHeight: 1.6 }}
            >
              Please find below a list of the most commonly occurring ranges
              used by our clients/practices nationally.
              <br />
              Category — <strong style={{ color: "#333" }}>{category}</strong>
              <br />
              Please verify the ranges before applying them.
            </Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />

          {/* Loading */}
          {loading && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1.5,
                py: 3,
              }}
            >
              <CircularProgress size={20} sx={{ color: "#7c4dff" }} />
              <Typography sx={{ fontSize: "0.8rem", color: "#888" }}>
                Fetching suggestions…
              </Typography>
            </Box>
          )}

          {/* Error */}
          {!loading && !!error && (
            <Alert severity="error" sx={{ fontSize: "0.8rem" }}>
              {error}
            </Alert>
          )}

          {/* Suggestions table */}
          {!loading && !error && (
            <>
              {!suggestions.length ? (
                <Alert severity="info" sx={{ fontSize: "0.8rem" }}>
                  No suggestions available.
                </Alert>
              ) : (
                <>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mb: 1 }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={allSelected}
                          onChange={toggleSelectAll}
                          sx={{
                            color: "#7c4dff",
                            "&.Mui-checked": { color: "#7c4dff" },
                          }}
                        />
                      }
                      label={
                        <Typography
                          sx={{
                            fontSize: "0.8rem",
                            fontWeight: 600,
                            color: "#444",
                          }}
                        >
                          Select All
                        </Typography>
                      }
                    />
                    <Chip
                      label={`${selected.length} selected`}
                      size="small"
                      sx={{
                        background: "#ede7f6",
                        color: "#4527a0",
                        fontWeight: 600,
                        fontSize: "0.75rem",
                      }}
                    />
                  </Stack>

                  <Box
                    sx={{
                      border: "1.5px solid #ede7f6",
                      borderRadius: "8px",
                      overflow: "hidden",
                    }}
                  >
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ background: "#f8f9ff" }}>
                          <TableCell padding="checkbox" />
                          <TableCell
                            sx={{
                              fontSize: "0.7rem",
                              fontWeight: 700,
                              textTransform: "uppercase",
                              letterSpacing: "0.07em",
                              color: "#7c4dff",
                            }}
                          >
                            Gender / Age
                          </TableCell>
                          <TableCell
                            sx={{
                              fontSize: "0.7rem",
                              fontWeight: 700,
                              textTransform: "uppercase",
                              letterSpacing: "0.07em",
                              color: "#7c4dff",
                            }}
                          >
                            Min Value
                          </TableCell>
                          <TableCell
                            sx={{
                              fontSize: "0.7rem",
                              fontWeight: 700,
                              textTransform: "uppercase",
                              letterSpacing: "0.07em",
                              color: "#7c4dff",
                            }}
                          >
                            Max Value
                          </TableCell>
                          <TableCell
                            sx={{
                              fontSize: "0.7rem",
                              fontWeight: 700,
                              textTransform: "uppercase",
                              letterSpacing: "0.07em",
                              color: "#7c4dff",
                            }}
                          >
                            No of Practices
                          </TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {suggestions.map((row, idx) => (
                          <TableRow
                            key={idx}
                            hover
                            sx={{
                              cursor: "pointer",
                              background: selected.includes(idx)
                                ? "#f8f9ff"
                                : "transparent",
                              "&:hover": { background: "#f3e5f5" },
                            }}
                            onClick={() => toggleRow(idx)}
                          >
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={selected.includes(idx)}
                                onChange={() => toggleRow(idx)}
                                onClick={(e) => e.stopPropagation()}
                                sx={{
                                  color: "#7c4dff",
                                  "&.Mui-checked": { color: "#7c4dff" },
                                }}
                              />
                            </TableCell>

                            <TableCell>
                              <Chip
                                label={buildLabel(row)}
                                size="small"
                                sx={{
                                  background: "#e8eaf6",
                                  color: "#283593",
                                  fontWeight: 500,
                                  fontSize: "0.75rem",
                                  height: 22,
                                  border: "1px solid #c5cae9",
                                }}
                              />
                            </TableCell>

                            <TableCell>
                              <Typography
                                sx={{ fontSize: "0.82rem", color: "#333" }}
                              >
                                {row.min_val ?? "—"}
                              </Typography>
                            </TableCell>

                            <TableCell>
                              <Typography
                                sx={{ fontSize: "0.82rem", color: "#333" }}
                              >
                                {row.max_val ?? "—"}
                              </Typography>
                            </TableCell>

                            <TableCell>
                              <Chip
                                label={row.found_count ?? "—"}
                                size="small"
                                sx={{
                                  background: "#ede7f6",
                                  color: "#4527a0",
                                  fontWeight: 700,
                                  fontSize: "0.75rem",
                                  height: 22,
                                }}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                </>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 2.5, pb: 2, pt: 1, gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={handleClose}
            sx={{
              fontSize: "0.78rem",
              borderColor: "#ccc",
              color: "#666",
              textTransform: "none",
              borderRadius: "6px",
            }}
          >
            Ignore & Close
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleApply}
            disabled={!selectedRows.length}
            startIcon={<CheckCircleOutlineIcon sx={{ fontSize: 15 }} />}
            sx={{
              fontSize: "0.78rem",
              background: "linear-gradient(135deg, #7c4dff 0%, #5c35cc 100%)",
              color: "#fff",
              textTransform: "none",
              borderRadius: "6px",
              fontWeight: 600,
              boxShadow: "0 2px 8px rgba(124,77,255,0.3)",
              "&:hover": {
                background: "linear-gradient(135deg, #6a3de8 0%, #4a2ab0 100%)",
              },
              "&:disabled": {
                background: "#e0e0e0",
                color: "#aaa",
                boxShadow: "none",
              },
            }}
          >
            Apply Selected
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SubtestRangeRecommendButton;
